import ChatWindow from 'components/ChatBox/ ChatWindow';
import CircularProgress from 'components/CircularProgress';
import DefaultModal from 'components/common/DefaultModal';
import { DEFAULT_FORMAT_DATE_US } from 'constants/days';
import deviceSizes from 'constants/deviceSizes';
import useInfiniteMessageQuery from 'hooks/useInfiniteMessageQuery';
import moment from 'moment';
import React, { useEffect } from 'react';

import { ITransactionTabsProps } from '../types';
import AppointmentInfoModalContent from './AppointmentInfoModalContent';
import AppointmentListInMessageTab from './AppointmentListInMessageTab';
import { useTransactionMessageTabProps } from './hooks';
import MobileAppointmentListInMessageTab from './MobileAppointmentListInMessageTab';

const TransactionMessageTab: React.FC<ITransactionTabsProps> = ({
  transactionData,
  chatRoomData,
  chatRoomLoading,
  refetchChatRoomData,
}) => {
  const mobileThreshold = deviceSizes.numbers.md;
  const {
    onAppointmentClick,
    selectedAppointment,
    selectedChatRoom,
    setSelectedChatRoom,
    AppointmentChatWindowHeader,
    appointmentInfoModalVisible,
    closeAppointmentInfoModal,
    isUserInSelectedChatRoom,
  } = useTransactionMessageTabProps({ chatRooms: chatRoomData });

  useEffect(() => {
    if (chatRoomData?.length === 1) {
      setSelectedChatRoom(chatRoomData[0]);
    }
  }, [transactionData]);

  const {
    data: messages,
    loading: messageLoading,
    oldMessageLoading,
    oldMessageData,
    fetchOldMessages,
    subscribeToMessages,
    refetch: refetchMessageData,
  } = useInfiniteMessageQuery({
    chatRoomId: selectedChatRoom?.id,
  });

  useEffect(() => {
    const unsub = subscribeToMessages(selectedChatRoom?.id || '');
    return () => unsub();
  }, [selectedChatRoom?.id]);

  return (
    <>
      <DefaultModal
        props={{
          title: `Appointment ${moment(selectedAppointment?.date).format(DEFAULT_FORMAT_DATE_US)}`,
          visible: appointmentInfoModalVisible,
          onCancel: closeAppointmentInfoModal,
        }}>
        <AppointmentInfoModalContent
          refetchChatRoom={refetchChatRoomData}
          chatRoom={selectedChatRoom}
          appointment={selectedAppointment}
          signatories={transactionData?.signatories}
        />
      </DefaultModal>
      <div className='flex max-h-full h-full relative'>
        <div
          className='border-r border-border_color h-full overflow-y-auto hidden md:block'
          style={{ flex: '1 0 300px' }}>
          {chatRoomLoading && !chatRoomData?.length && <CircularProgress />}
          {chatRoomData?.length ? (
            <AppointmentListInMessageTab
              refetchChatRoom={refetchChatRoomData}
              selectedChatRoom={selectedChatRoom}
              transaction={transactionData}
              onAppointmentClick={onAppointmentClick}
              chatRooms={chatRoomData}
            />
          ) : (
            !chatRoomLoading && <div className='p-4 h-full w-full text-xl'>No Appointment</div>
          )}
        </div>
        <MobileAppointmentListInMessageTab
          transaction={transactionData}
          onAppointmentClick={onAppointmentClick}
          chatRooms={chatRoomData}
          selectedChatRoom={selectedChatRoom}
          loading={chatRoomLoading}
        />
        {selectedChatRoom ? (
          <ChatWindow
            oldMessages={oldMessageData}
            oldMessageLoading={oldMessageLoading}
            loading={messageLoading}
            messages={messages}
            fetchNextPageMessage={fetchOldMessages}
            chatRoom={selectedChatRoom}
            refetchChatRoomData={refetchChatRoomData}
            header={AppointmentChatWindowHeader}
            mobileThreshold={mobileThreshold}
            allowJoinChat
            isInChatRoom={isUserInSelectedChatRoom}
            refetchMessageData={refetchMessageData}
          />
        ) : (
          <div className='p-4 h-full w-full text-xl opacity-0 md:opacity-100'>
            Select an appointment to view message thread.
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(TransactionMessageTab);
